import React, { useEffect, useRef, useState } from "react";
import Duck from "@/components/common/3D/Duck";
import { useDuck, AnimationStateKeys } from "@/provider/DuckContext";
import gsap from "gsap";
import ServicesStyles from "@/components/Services/OurServices/OurServices.module.scss";
import { useThree } from "@react-three/fiber";
import * as Helpers from "./InnovationDuckHelpers";
import FollowWave from "@/components/common/3D/FollowWave";
import { Euler, Vector3 } from "three";
import { useGLTF } from "@react-three/drei";

export function InnovationDuck({ waveRef }) {
  const { viewport } = useThree();
  const sceneRef = useRef(null);

  const { handleDuckAnimation } = useDuck();

  const { scene: romanHelmet } = useGLTF("/models/roman_helmet.glb");
  const { scene: baseballCap } = useGLTF("/models/baseball_cap.glb");
  const { scene: scrubCap } = useGLTF("/models/scrub_cap.glb");
  const { scene: detectiveHat } = useGLTF("/models/detective_hat.glb");
  const { scene: safetyHelmet } = useGLTF("/models/safety_helmet.glb");

  const duckRef = useRef(null);
  const romanHelmentRef = useRef(null);
  const baseballCapRef = useRef(null);
  const scrubCapRef = useRef(null);
  const detectiveHatRef = useRef(null);
  const safetyHelmetRef = useRef(null);

  const [romanHelmetModel, setRomanHelmentModel] = useState(null);
  const [baseballCapModel, setBaseballCapModel] = useState(null);
  const [scrubCapModel, setScrubCapModel] = useState(null);
  const [detectiveHatModel, setDetectiveHatModel] = useState(null);
  const [safetyHelmetModel, setSafetyHelmetModel] = useState(null);

  const [movement, setMovement] = useState(false);

  //Hats initial set
  useEffect(() => {
    if (
      romanHelmet === null ||
      baseballCap === null ||
      scrubCap === null ||
      detectiveHat === null ||
      safetyHelmet === null
    )
      return;

    const models = [
      { scene: romanHelmet, callback: setRomanHelmentModel },
      { scene: baseballCap, callback: setBaseballCapModel },
      { scene: scrubCap, callback: setScrubCapModel },
      { scene: detectiveHat, callback: setDetectiveHatModel },
      { scene: safetyHelmet, callback: setSafetyHelmetModel },
    ];

    Helpers.InitHatModels(models);
  }, [romanHelmet, baseballCap, scrubCap, safetyHelmet, detectiveHat]);

  //GSAP Animations
  useEffect(() => {
    if (
      sceneRef.current !== null &&
      romanHelmentRef.current !== null &&
      baseballCapRef.current !== null &&
      detectiveHatRef.current !== null
    ) {
      const pinTimeline = gsap.timeline({
        scrollTrigger: {
          trigger: ".innovationCanvas",
          start: "top top",
          endTrigger: `.${ServicesStyles.ourServiceswrap}`,
          end: "bottom bottom",
          pin: true,
          invalidateOnRefresh: true,
          fastScrollEnd: true,
          onEnter: (e) => {
            // scrolltrigger.refresh();
            handleDuckAnimation(AnimationStateKeys.GREETINGS);
          },
        },
      });

      let modelTimelineOptions = {
        listItemIndex: 1,
        end: "center bottom",
      };

      if (typeof window !== undefined) {
        if (window.innerWidth > 1500) {
          modelTimelineOptions = {
            listItemIndex: 2,
            end: "top-=200 bottom",
          };
        }
      }

      const modelTimeline = gsap.timeline({
        scrollTrigger: {
          trigger: `.${ServicesStyles.ourServiceswrap} > div > div`,
          start: "bottom bottom",
          endTrigger: `.${ServicesStyles.ourListDuck} > ul > li:nth-child(${modelTimelineOptions.listItemIndex})`,
          end: `${modelTimelineOptions.end} bottom`,
          scrub: true,
          invalidateOnRefresh: true,
          fastScrollEnd: true,
        },
      });

      modelTimeline
        .to(sceneRef.current.position, {
          x: -viewport.width / 3.5,
          y: -viewport.height / 4,
          ease: "sine.in",
        })
        .to(
          sceneRef.current.rotation,
          {
            y: 0.9,
            ease: "sine.in",
          },
          "<"
        );

      const initialHat = gsap.timeline({
        scrollTrigger: {
          trigger: `.${ServicesStyles.ourListDuck} > ul > li:nth-child(1)`,
          start: "top center",
          onEnter: () => {
            Helpers.simpleHatTween(romanHelmentRef.current.position, 25, 0.5);
          },
          onLeaveBack: () => {
            Helpers.simpleHatTween(romanHelmentRef.current.position, 190, 0.8);
          },
        },
      });

      // prettier-ignore
      const hatTransitions = [
        Helpers.createHatTransition(2, {target: sceneRef.current.rotation, rotation: Math.PI * 2 + 0.9},
          {target: romanHelmentRef.current.position, yOut: 190, yIn: 25, duration: 0.8},
          {target: baseballCapRef.current.position, yOut: 195, yIn: 15, duration: 0.5}, handleDuckAnimation),
        Helpers.createHatTransition(3,{target: sceneRef.current.rotation, rotation: Math.PI * 4 + 0.9},
          {target: baseballCapRef.current.position, yOut: 195, yIn: 15, duration: 0.8},
          {target: scrubCapRef.current.position, yOut: 195, yIn: 31, duration: 0.5}, handleDuckAnimation),
        Helpers.createHatTransition(4, {target: sceneRef.current.rotation, rotation: Math.PI * 6 + 0.9},
          {target: scrubCapRef.current.position, yOut: 195, yIn: 31, duration: 0.8},
          {target: detectiveHatRef.current.position, yOut: 195, yIn: 40, duration: 0.5}, handleDuckAnimation),
        Helpers.createHatTransition(5, {target: sceneRef.current.rotation, rotation: Math.PI * 8 + 0.9},
          {target: detectiveHatRef.current.position, yOut: 195, yIn: 40, duration: 0.8},
          {target: safetyHelmetRef.current.position, yOut: 195, yIn: 27, duration: 0.5}, handleDuckAnimation),
      ];

      return () => {
        pinTimeline?.scrollTrigger?.kill();
        pinTimeline?.kill();

        modelTimeline?.scrollTrigger?.kill();
        modelTimeline?.kill();

        initialHat?.scrollTrigger?.kill();
        initialHat?.kill();

        hatTransitions.forEach((tl) => {
          tl?.scrollTrigger?.kill();
          tl?.kill();
        });
      };
    }
  }, [
    sceneRef.current,
    romanHelmentRef.current,
    baseballCapRef.current,
    detectiveHatRef.current,
    safetyHelmetRef.current,
  ]);

  //Integrate hats into the duck
  useEffect(() => {
    if (
      duckRef.current?.children.length > 0 &&
      sceneRef.current !== null &&
      romanHelmetModel &&
      baseballCapModel &&
      scrubCapModel &&
      detectiveHatModel &&
      safetyHelmetModel
    ) {
      const duckHead =
        duckRef.current.children[0].children[0].children[11].children[0]
          .children[3].children[0];

      if (duckHead) {
        // prettier-ignore
        const models = [
          {scene: romanHelmetModel, ref: romanHelmentRef, scale: 38.5,
            position: { y: 190, z: -17.5 }, rotation: { x: 0, y: 0 }},
          {scene: baseballCapModel, ref: baseballCapRef, scale: 215, 
            position: { y: 195, z: -5 }, rotation: { x: -0.25, y: Math.PI }},
          {scene: scrubCapModel, ref: scrubCapRef, scale: 40,
            position: { y: 195, z: -10 }, rotation: { x: 0, y: 0 }},
          {scene: detectiveHatModel, ref: detectiveHatRef, scale: 20,
            position: { y: 195, z: -5 }, rotation: { x: 0, y: 0 }},
          {scene: safetyHelmetModel, ref: safetyHelmetRef, scale: 19,
            position: { y: 195, z: -3 }, rotation: { x: 0, y: 0 }},
        ];

        Helpers.IntegrateHatModels(duckHead, duckRef, models);

        setMovement(true);
      }
    }
  }, [
    duckRef.current?.children,
    romanHelmetModel,
    baseballCapModel,
    scrubCapModel,
    detectiveHatModel,
    safetyHelmetModel,
    sceneRef,
  ]);

  const [duckProps, setDuckProps] = useState({
    scale: 0,
    position: new Vector3(),
    rotation: new Euler(),
  });
  const [isSetted, setIsSetted] = useState(false);

  useEffect(() => {
    if (viewport && !isSetted) {
      const duckProps = calculateDuckProps(viewport);
      setDuckProps(duckProps);
      setIsSetted(true);
    }
  }, [viewport, isSetted]);

  return (
    <>
      <FollowWave
        wave={waveRef.current?.lastChild}
        inverse={true}
        positionOffset={-0.75}
      >
        <group
          scale={duckProps.scale}
          ref={sceneRef}
          position={duckProps.position}
          rotation={duckProps.rotation}
        >
          <group scale={1} ref={duckRef}>
            <Duck cursorAnimations={movement} cursorAngleLimit={12} />
          </group>
        </group>
      </FollowWave>
    </>
  );
}

function calculateDuckProps(viewport) {
  const scale = viewport.height / 1.65;
  const position = new Vector3(viewport.width / 4, -viewport.height / 4, 0);
  const rotation = new Euler(0, -1.1, 0);

  return { scale, position, rotation };
}

useGLTF.preload([
  "/models/roman_helmet.glb",
  "/models/baseball_cap.glb",
  "/models/scrub_cap.glb",
  "/models/detective_hat.glb",
  "/models/safety_helmet.glb",
]);
